import { useState, useMemo } from 'react'
import { Download, CheckCircle, ChevronDown, ChevronUp, Package } from 'lucide-react'
import { formatNaira } from '../../utils/formatters'

const STATUS_COLORS = {
  open: 'bg-blue-100 text-blue-700',
  exported: 'bg-purple-100 text-purple-700',
  reconciled: 'bg-green-100 text-green-700',
}

function BatchSaleRow({ sale }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0 text-xs">
      <div className="min-w-0 flex-1">
        <p className="font-medium text-gray-700 truncate">{sale.companyName}</p>
        <p className="text-gray-400">{sale.driverName} · {sale.productType} · {sale.litres.toLocaleString('en-NG')} L</p>
      </div>
      <p className="font-bold text-gray-800 ml-2 flex-shrink-0">{formatNaira(sale.totalValue)}</p>
    </div>
  )
}

export default function WeeklyBatchPanel({ currentUser, branches, batches, sales, onExport, onReconcile }) {
  const isAdmin = currentUser.role === 'admin' || currentUser.role === 'superadmin'
  const [expandedId, setExpandedId] = useState(null)
  const [branchFilter, setBranchFilter] = useState(currentUser.branchId || 'all')
  const [confirming, setConfirming] = useState(null) // batchId for reconcile confirm

  const visibleBranches = currentUser.role === 'manager'
    ? branches.filter((b) => b.id === currentUser.branchId)
    : branches

  const filteredBatches = useMemo(() => {
    return batches
      .filter((b) => branchFilter === 'all' || b.branchId === branchFilter)
      .sort((a, b) => new Date(b.weekStart) - new Date(a.weekStart))
  }, [batches, branchFilter])

  function batchSales(batchId) {
    return sales.filter((s) => s.weeklyBatchId === batchId)
      .sort((a, b) => new Date(a.saleTime) - new Date(b.saleTime))
  }

  function batchTotals(batchSalesList) {
    return {
      count: batchSalesList.length,
      litres: batchSalesList.reduce((s, r) => s + r.litres, 0),
      value: batchSalesList.reduce((s, r) => s + r.totalValue, 0),
    }
  }

  function formatWeekRange(weekStart, weekEnd) {
    const opts = { day: '2-digit', month: 'short' }
    return `${new Date(weekStart).toLocaleDateString('en-NG', opts)} – ${new Date(weekEnd).toLocaleDateString('en-NG', opts)}`
  }

  async function handleExport(batch) {
    const bSales = batchSales(batch.id)
    const branch = branches.find((b) => b.id === batch.branchId)
    const weekStartStr = batch.weekStart.slice(0, 10).replace(/-/g, '-')
    const weekEndStr = batch.weekEnd.slice(0, 10).replace(/-/g, '-')

    const headers = ['Receipt No', 'Date', 'Company', 'Driver', 'Plate No', 'Destination', 'Product', 'Litres', 'Unit Price (NGN)', 'Total Value (NGN)']
    const rows = bSales.map((s) => {
      const date = new Date(s.saleTime).toLocaleDateString('en-NG')
      return [
        s.receiptNumber, date, s.companyName, s.driverName,
        s.licencePlate || '', s.destination, s.productType,
        s.litres, s.unitPriceAtSale, s.totalValue,
      ].join(',')
    })
    const totRow = ['', '', '', '', '', '', 'TOTAL', batchSales(batch.id).reduce((s, r) => s + r.litres, 0), '', batchSales(batch.id).reduce((s, r) => s + r.totalValue, 0)]

    const branchSlug = (branch?.name || 'Branch').replace(/[^a-zA-Z0-9]/g, '_')
    const csv = [
      branch?.name || '',
      `Week: ${weekStartStr} to ${weekEndStr}`,
      '',
      headers.join(','),
      ...rows,
      totRow.join(','),
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${branchSlug}_Week_${weekStartStr}_${weekEndStr}.csv`
    a.click()
    URL.revokeObjectURL(url)

    await onExport(batch.id)
  }

  async function handleReconcile(batchId) {
    await onReconcile(batchId)
    setConfirming(null)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-gray-800">Weekly Batches</h2>
          <p className="text-xs text-gray-400">{filteredBatches.length} batch{filteredBatches.length !== 1 ? 'es' : ''}</p>
        </div>
        {currentUser.role !== 'manager' && (
          <select
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="all">All branches</option>
            {visibleBranches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
      </div>

      {filteredBatches.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
          <Package className="w-10 h-10 text-gray-200 mx-auto mb-2" />
          <p className="text-sm text-gray-400">No batches yet. Sales auto-batch every Sunday.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredBatches.map((batch) => {
            const bSales = batchSales(batch.id)
            const totals = batchTotals(bSales)
            const isExpanded = expandedId === batch.id
            const branch = branches.find((b) => b.id === batch.branchId)

            return (
              <div key={batch.id} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <button
                  onClick={() => setExpandedId(isExpanded ? null : batch.id)}
                  className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center space-x-2 mb-0.5">
                      <p className="font-semibold text-sm text-gray-800">{formatWeekRange(batch.weekStart, batch.weekEnd)}</p>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${STATUS_COLORS[batch.status] || 'bg-gray-100 text-gray-600'}`}>
                        {batch.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400">{branch?.name} · {totals.count} sales · {totals.litres.toLocaleString('en-NG')} L</p>
                  </div>
                  <div className="ml-3 text-right flex-shrink-0 flex items-center space-x-2">
                    <p className="text-sm font-bold text-gray-800">{formatNaira(totals.value)}</p>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-gray-100 px-4 py-3 space-y-3">
                    {/* Action buttons */}
                    {isAdmin && batch.status !== 'reconciled' && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleExport(batch)}
                          className="flex-1 flex items-center justify-center space-x-1.5 py-2.5 rounded-xl bg-gray-800 text-white text-sm font-semibold"
                        >
                          <Download className="w-4 h-4" /><span>Export CSV</span>
                        </button>
                        {batch.status === 'exported' && (
                          confirming === batch.id ? (
                            <div className="flex-1 flex space-x-2">
                              <button
                                onClick={() => handleReconcile(batch.id)}
                                className="flex-1 py-2.5 rounded-xl bg-green-600 text-white text-sm font-semibold"
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => setConfirming(null)}
                                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setConfirming(batch.id)}
                              className="flex-1 flex items-center justify-center space-x-1.5 py-2.5 rounded-xl bg-green-600 text-white text-sm font-semibold"
                            >
                              <CheckCircle className="w-4 h-4" /><span>Reconcile</span>
                            </button>
                          )
                        )}
                      </div>
                    )}

                    {batch.status === 'reconciled' && batch.reconciledAt && (
                      <p className="text-xs text-green-700 bg-green-50 rounded-lg px-3 py-2">
                        Reconciled on {new Date(batch.reconciledAt).toLocaleDateString('en-NG', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </p>
                    )}

                    {/* Sales list */}
                    <div className="max-h-64 overflow-y-auto">
                      {bSales.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-3">No sales in this batch.</p>
                      ) : (
                        bSales.map((s) => <BatchSaleRow key={s.id} sale={s} />)
                      )}
                    </div>

                    {/* Totals row */}
                    {bSales.length > 0 && (
                      <div className="flex justify-between pt-1 border-t border-gray-100">
                        <span className="text-xs font-semibold text-gray-600">Total ({totals.count} sales, {totals.litres.toLocaleString('en-NG')} L)</span>
                        <span className="text-sm font-bold text-gray-800">{formatNaira(totals.value)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
